import {
  RequestConfig,
  ResponseConfig,
  SecurityConfig,
  SecuritySDKProps,
  ProxyHooksResult,
  EventEmitParams,
  EventErrorParams,
  EventLogParams
} from '@byted/tiktok-zti-types';
import { logFullPath, logPerformance } from '@byted/tiktok-zti-monitor';
import { setSignTimeout, setCacheSignString, getCacheSignString } from '../cache';
import { ProxyConfig } from '../types';
import { formatUrl, isPublishKeySign, checkCertIsNewVersion } from '../utils';

export interface EventMap {
  reportEvent: (args: EventEmitParams) => void;
  reportError: (args: EventErrorParams) => void;
  reportLog: (args: EventLogParams) => void;
}

export const processSignData = (
  request: RequestConfig,
  ticket: string,
  urlRewriteRules?: string[][],
  event?: EventMap
): { req_content: string; sign_data: string; timestamp?: number } => {
  try {
    const { url } = request || {};
    if (!url) {
      return { req_content: '', sign_data: '' };
    }
    const timeStamp = Math.floor(new Date().getTime() / 1000);
    const urlObj = new URL(url, window.location.href);
    const { pathname } = urlObj || {};
    let path = pathname;
    // ticket={ticket}&path={path}&timestamp={timestamp}
    if (urlRewriteRules && urlRewriteRules.length > 0) {
      urlRewriteRules.forEach((ele) => {
        if (ele instanceof Array && ele.length > 1) {
          const origin_path = ele[0];
          const rewrite_path = ele[1];
          const reg = new RegExp(origin_path);
          if (url.match(reg)) {
            path = rewrite_path;
          }
        }
      });
    }
    // event?.reportEvent({ action: 'request', op: 'process', status: 'success', ctx: { type: 'sign' } });
    return {
      req_content: 'ticket,path,timestamp',
      sign_data: `ticket=${ticket}&path=${path}&timestamp=${timeStamp}`,
      timestamp: timeStamp
    };
  } catch (error) {
    // event?.reportEvent({ action: 'request', op: 'process', status: 'fail', ctx: { type: 'sign' } });
    event?.reportError({ error, name: 'request process sign data fail' });
    return { req_content: '', sign_data: '' };
  }
};

const processStorageStatusData = (data: any): string => {
  if (typeof data === 'number') {
    return data.toString();
  }
  return '-99';
};

/**
 * 获取当前请求配置
 * @param xhrConfig request config
 * @returns needProxy & proxyConfig  needProxy表示是否需要代理  proxyConfig 代理配置
 */
export const getHooksConfig = (
  xhrConfig: RequestConfig,
  secureConfig: SecurityConfig,
  signType: 'cert' | 'pubKey',
  initType: 'cert' | 'pubKey'
): ProxyHooksResult => {
  try {
    if (!secureConfig) {
      return { needProxy: false };
    }
    const { url } = xhrConfig || {};
    if (!url) {
      return { needProxy: false };
    }
    let needProxy = false;
    let providerConfig: SecuritySDKProps | undefined, consumerConfig: SecuritySDKProps | undefined, proxyURL;
    Object.keys(secureConfig).forEach((key) => {
      const config = secureConfig[key];
      if (Array.isArray(config)) {
        config.forEach((element) => {
          const { providerPathList = [], consumerPathList = [] } = element || {};

          if (providerPathList.length > 0 && !providerConfig) {
            for (let index = 0; index < providerPathList.length; index++) {
              const ele = providerPathList[index];
              const reg = new RegExp(ele);
              if (url.match(reg)) {
                needProxy = true;
                providerConfig = element;
                proxyURL = ele;
                break;
              }
            }
          }
          if (consumerPathList.length > 0 && !consumerConfig) {
            for (let index = 0; index < consumerPathList.length; index++) {
              const ele = consumerPathList[index];
              const reg = new RegExp(ele);
              if (url.match(reg)) {
                needProxy = true;
                consumerConfig = element;
                proxyURL = ele;
                break;
              }
            }
          }
        });
      }
    });
    return { needProxy, providerConfig, consumerConfig, url: proxyURL, signType, initType };
  } catch (error) {
    return { needProxy: false };
  }
};
// used for add requestion ticket
export const processRequestConfig = async (
  requestConfig: RequestConfig,
  proxyConfig: ProxyConfig,
  hooksResult: ProxyHooksResult,
  event: EventMap
): Promise<RequestConfig> => {
  try {
    const { headers } = requestConfig || {};
    const { crypt } = proxyConfig;
    if (!headers) {
      return Promise.resolve(requestConfig);
    }
    if (typeof headers === 'string') {
      return Promise.resolve(requestConfig);
    }
    // 判断当前startTimes 是否正常
    const startTimes = Date.now();
    const {
      needProxy,
      consumerConfig,
      providerConfig,
      url,
      signType = 'pubKey',
      initType = 'pubKey'
    } = hooksResult;
    // 存在bug，可能有一些接口即使消费者也是生产者 目前没有这个case暂不解决
    const {
      scene = '',
      certType = 'header',
      signTimeout,
      namespace
    } = consumerConfig || providerConfig || {};
    const provider = Boolean(providerConfig);
    if (needProxy && crypt) {
      let useCacheSign = false;
      const keysInfo = await crypt?.getKeysInfoWithOrigin({ certType, scene, namespace });
      const { match_md5_iframe, match_md5_local } = await crypt?.checkSignData(keysInfo);

      const { ec_publicKey, ec_csr } = keysInfo?.crypt || {};
      const { ticket, ts_sign } = keysInfo?.sign || {};
      const { cert, b64Cert, dataFrom, b64PubKey, b64Csr } = keysInfo || {};
      let serverData;
      let dataLost = '0';
      const cryptStatus = crypt?.getCookieCryptStatus();
      if (!ticket && !ts_sign && cryptStatus) {
        dataLost = '1';
      }
      if (ticket && ec_publicKey && consumerConfig && url) {
        signTimeout && setSignTimeout(signTimeout);
        const str = getCacheSignString(url, ticket);
        if (str) {
          serverData = str;
          useCacheSign = true;
        } else {
          const { req_content, sign_data, timestamp } = processSignData(
            requestConfig,
            ticket,
            consumerConfig?.urlRewriteRules || [],
            event
          );
          serverData = await crypt?.signWithKeysInfo({
            sign_data,
            req_content,
            timestamp,
            certType,
            scene,
            keysInfo
          });
          // serverData = await crypt?.sign({ sign_data, req_content, timestamp, certType, scene });
          if (serverData) {
            setCacheSignString(url, ticket, serverData);
            event.reportLog({
              extra: { content: serverData, url, ticket },
              content: 'set cache data success',
              level: 'info'
            });
          }
        }
      }
      let certHeader: { [key: string]: unknown } = {};
      // 公钥验签 + 命中实验 ||  当前的证书是cert 如果是新版证书直接走新流程

      // 如果是ticket提供的路径，则需要遵循生成签名逻辑
      if (provider) {
        if (initType === 'pubKey') {
          certHeader = {
            'tt-ticket-guard-public-key': b64PubKey,
            'tt-ticket-guard-web-version': 1
          };
        } else {
          // useless
          certHeader = b64Cert
            ? {
                'tt-ticket-guard-client-cert': b64Cert || ''
              }
            : { 'tt-ticket-guard-client-csr': b64Csr || '' };
        }
      } else {
        // 签名生成逻辑
        // 如果是ts.1 开头的sign，或者本地cert已经修改pub.开头的，则必须走公钥验签，否则会有问题
        if ((isPublishKeySign(ts_sign || '') && signType === 'pubKey') || checkCertIsNewVersion(cert)) {
          certHeader = {
            'tt-ticket-guard-public-key': b64PubKey,
            'tt-ticket-guard-web-version': 1
          };
        } else {
          // useless
          certHeader = b64Cert
            ? {
                'tt-ticket-guard-client-cert': b64Cert || ''
              }
            : { 'tt-ticket-guard-client-csr': b64Csr || '' };
        }
      }

      requestConfig.headers = {
        ...requestConfig.headers,
        ...certHeader,
        'tt-ticket-guard-version': consumerConfig?.signVersion || providerConfig?.signVersion || 1,
        'tt-ticket-guard-iteration-version': 0
      };
      if (serverData) {
        requestConfig.headers = {
          ...requestConfig?.headers,
          'tt-ticket-guard-client-data': serverData
        };
      }
      const endTimes = Date.now();
      event.reportLog({
        extra: { content: JSON.stringify(requestConfig), time: new Date().getTime() },
        content: 'process request config ',
        level: 'info'
      });
      const storageStatus = crypt?.getStorageStatus();
      const { isConnection, retryCount, startTime, endTime, loadTime } = storageStatus || {};

      event.reportEvent({
        action: 'request',
        op: 'sign',
        duration: endTimes > startTimes ? endTimes - startTimes : 0,
        status: 'success',
        ctx: {
          cache: useCacheSign ? '1' : '0',
          path: url || '',
          cert: cert ? '1' : '0',
          pubKey: ec_publicKey ? '1' : '0',
          isPubKeySign: isPublishKeySign(ts_sign || '') ? '1' : '0',
          isPubKeyInit: initType === 'pubKey' ? '1' : '0',
          csr: ec_csr ? '1' : '0',
          version: `${consumerConfig?.signVersion || ''}` || `${providerConfig?.signVersion || ''}` || '1',
          server: serverData ? '1' : '0',
          crossStatus: crypt?.getIframeStatus() ? '1' : '0',
          initMatch: crypt?.initMatch ? '1' : '0',
          dataFrom: dataFrom || '-99',
          match_md5_local: match_md5_local || '-99',
          match_md5_iframe: match_md5_iframe || '-99',
          lost: dataLost,
          isNewCert: checkCertIsNewVersion(cert) ? '1' : '0',
          isConnection: processStorageStatusData(isConnection),
          retryCount: processStorageStatusData(retryCount)
        },
        metrics: { startTime: startTime || 0, endTime: endTime || 0, loadTime: loadTime || 0 }
      });
      requestConfig.extras = {
        ...(keysInfo || {}),
        process_request_duration: endTimes - startTimes,
        scene,
        certType,
        match_md5_iframe,
        match_md5_local,
        is_pubkey_ts_sign: isPublishKeySign(ts_sign || '') ? '1' : '0',
        // 上报是否新证书模式
        is_new_cert: checkCertIsNewVersion(cert) ? '1' : '0',
        lost: dataLost,
        isPubKeyInit: initType === 'pubKey' ? '1' : '0'
      };
    }
  } catch (error) {
    event.reportError({ error, name: 'process request config fail' });
    event.reportLog({
      content: 'process request config fail',
      extra: { content: JSON.stringify(requestConfig) }
    });
    event.reportEvent({
      action: 'request',
      op: 'sign',
      status: 'fail',
      ctx: {
        path: requestConfig?.url || ''
      }
    });
  }
  return Promise.resolve(requestConfig);
};

// used for processResponseConfig
export const processRequestHeader = (
  verify: string,
  keysInfo: { [key: string]: any },
  response: ResponseConfig,
  proxyConfig: ProxyConfig,
  updateData: boolean,
  login: boolean,
  event: EventMap
) => {
  try {
    const { crypt } = proxyConfig;
    const { certType, items = [] } = keysInfo || {};
    const ctx: { [key: string]: string } = {};
    const keys: string[] = [];
    const values: string[] = [];
    const requestWithClientData = response?.reqHeaders?.['tt-ticket-guard-client-data'] ? '1' : '0';
    const { url } = response?.config || {};
    if (certType === 'header') {
      return {};
    } else if (certType === 'cookie') {
      items &&
        Array.isArray(items) &&
        items.forEach((ele) => {
          const { key, value, from, origin } = ele || {};
          ctx[`${key.replace(/\//g, '_')}_origin`] = origin || '';
          ctx[`${key.replace(/\//g, '_')}_from`] = from || '-99';
          ctx[`${key.replace(/\//g, '_')}_status`] = value ? 'success' : 'success_null';
          if (value) {
            keys.push(key);
            values.push(value);
          }
        });
      if (verify === '-99' && updateData && url) {
        const needUpdateUrls = [
          '/aweme/v1/web/commit/item/digg',
          '/aweme/v1/web/commit/follow/user',
          '/aweme/v1/web/comment/publish',
          '/web/api/media/aweme/create'
        ];
        for (let index = 0; index < needUpdateUrls.length; index++) {
          const ele = needUpdateUrls[index];
          const reg = new RegExp(ele);
          if (url.match(reg)) {
            keys.length > 0 && crypt.setKeysAndValues(keys, values);
            break;
          }
        }
      }
    }
    const ticketGuardVersion = response?.reqHeaders?.['tt-ticket-guard-version'] || '-99';
    const ticketGuardIterationVersion = response?.reqHeaders?.['tt-ticket-guard-iteration-version'] || '-99';
    const requestWithCsr = response?.reqHeaders?.['tt-ticket-guard-client-csr'] ? '1' : '0';
    const requestWithCert = response?.reqHeaders?.['tt-ticket-guard-client-cert'] ? '1' : '0';
    const reqData = {
      csr: requestWithCsr,
      cert: requestWithCert,
      server: requestWithClientData,
      version: ticketGuardVersion,
      iterVersion: ticketGuardIterationVersion
    };
    return { ...reqData, ...ctx };
  } catch (error) {
    event.reportError({ error, name: 'process Request Header Error' });
  }
  return {};
};

// receive response, and verify tt-ticket-guard-result
export const processResponseConfig = async (
  response: ResponseConfig,
  proxyConfig: ProxyConfig,
  hooksResult: ProxyHooksResult,
  updateData: boolean,
  login: boolean,
  event: EventMap
) => {
  try {
    const { crypt } = proxyConfig;
    if (!response?.config?.headers) {
      return response;
    }
    if (typeof response?.config?.headers === 'string') {
      return response;
    }
    const { extras = {} } = response?.config || {};
    const {
      dataFrom,
      match_md5_local,
      match_md5_iframe,
      is_pubkey_ts_sign,
      is_new_cert,
      lost,
      isPubKeyInit
    } = extras || {};
    const storageStatus = crypt?.getStorageStatus();
    const { isConnection, retryCount, startTime, endTime, loadTime } = storageStatus || {};
    // 针对被票据sdk处理过的请求，做一次拦截，暴露出去给业务方
    if (response?.reqHeaders && response?.reqHeaders?.['tt-ticket-guard-version']) {
      event.reportEvent({
        action: 'response',
        op: 'sign',
        status: 'finish',
        ctx: {
          url: formatUrl(response?.config.url) || '',
          crossStatus: crypt?.getIframeStatus() ? '1' : '0',
          lost,
          dataFrom: dataFrom || '-99',
          match_md5_local,
          match_md5_iframe,
          initMatch: crypt?.initMatch ? '1' : '0',
          isConnection: processStorageStatusData(isConnection),
          retryCount: processStorageStatusData(retryCount),
          is_pubkey_ts_sign,
          is_new_cert,
          isPubKeyInit
        },
        metrics: { startTime: startTime || 0, endTime: endTime || 0, loadTime: loadTime || 0 },
        extras: response as Record<string, any>
      });
    }

    const startTimes = Date.now();
    const { needProxy, providerConfig, consumerConfig, signType = 'pubKey' } = hooksResult;
    const { scene, namespace } = providerConfig || {};
    if (needProxy && crypt) {
      const ts_sign_data: string = response?.headers?.['tt-ticket-guard-server-data'] || '';
      const verify_result = response?.headers?.['tt-ticket-guard-result'] || '-99';
      const sign_data = ts_sign_data && crypt?.b642str(ts_sign_data);
      // TODO: temp solution
      const data: any = sign_data && JSON.parse(sign_data).tickets[0];
      // let data: any = sign_data && JSON.parse(sign_data);
      const { ticket } = data || {};
      // 如果有namespace 证明需要重定向 不能走异步
      if (ticket && scene && namespace) {
        await crypt?.setSignValueAsync({ sign: data, scene, namespace });
      } else if (ticket && scene) {
        crypt?.setSignValue({ sign: data, scene });
      }
      const context = processRequestHeader(
        verify_result,
        extras,
        response,
        proxyConfig,
        updateData,
        login,
        event
      );
      const endTimes = Date.now();
      event.reportEvent({
        action: 'response',
        op: 'init',
        status: 'success',
        duration: endTimes > startTimes ? endTimes - startTimes : 0,
        ctx: {
          url: formatUrl(response?.config.url) || '',
          crossStatus: crypt?.getIframeStatus() ? '1' : '0',
          lost,
          verify: verify_result,
          dataFrom: dataFrom || '-99',
          match_md5_local,
          match_md5_iframe,
          server: ts_sign_data ? '1' : '0',
          client: extras?.serverData ? '1' : '0',
          isConnection: processStorageStatusData(isConnection),
          retryCount: processStorageStatusData(retryCount),
          initMatch: crypt?.initMatch ? '1' : '0',
          isNewCert: is_new_cert,
          isPubkeyTssign: is_pubkey_ts_sign,
          isPubKeyInit,
          ...(context || {})
        },
        metrics: { startTime: startTime || 0, endTime: endTime || 0, loadTime: loadTime || 0 }
      });
      logFullPath(
        response,
        ticket || response.config.extras?.sign?.ticket,
        consumerConfig ? 'consumer' : 'provider'
      );
      logPerformance({
        path: response.config.path,
        method: response.config.method ?? '',
        process_request_duration: response.config.extras?.process_request_duration,
        process_response_duration: endTimes - startTimes,
        sign_type: hooksResult.signType || '',
        store_type: crypt.storeType || ''
      });
    }
    return response;
  } catch (error) {
    event.reportEvent({
      action: 'response',
      op: 'init',
      status: 'fail',
      ctx: {
        server: response?.headers?.['tt-ticket-guard-server-data'] ? '1' : '0',
        client: (response?.config as any)?.serverData ? '1' : '0',
        url: formatUrl(response?.config.url) || ''
      }
    });
    event.reportError({ error, name: 'get sign data error in response' });
  }
  return response;
};

export const parseFetchHeader = (response: Response) => {
  const resHeaders: { [key: string]: any } = {};
  resHeaders['tt-ticket-guard-server-data'] = response?.headers?.get('tt-ticket-guard-server-data') || '';
  resHeaders['tt-ticket-guard-result'] = response?.headers?.get('tt-ticket-guard-result') || '';
  return resHeaders;
};
